import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell,
  BellRing,
  Menu,
  Search,
  LogOut,
  SlidersHorizontal,
  Sun,
  Moon,
  ChevronRight,
  CornerDownLeft,
  Check,
  ArrowRight,
  Settings,
} from "lucide-react";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { cn } from "@/lib/cn";
import { toastError, toastInfo, toastSuccess } from "@/lib/toast";
import { searchableNav, matchNavItem, ROLE_LABEL, HOME_BY_ROLE, NOTIF_PATH, SETTINGS_PATH } from "@/config/nav";
import {
  useNotifications,
  useNotificationsState,
  markRead,
  markAllRead,
  openNotificationLink,
  NOTIF_TYPES,
  timeAgo,
} from "@/lib/notifications";

const ICON_BTN =
  "relative grid h-10 w-10 place-items-center rounded-xl border border-slate-200 bg-white/70 text-slate-600 transition-all duration-200 hover:-translate-y-0.5 hover:border-brand-300 hover:text-brand-600 hover:shadow-soft active:translate-y-0 dark:border-white/10 dark:bg-white/5 dark:text-slate-300 dark:hover:border-brand-500/40 dark:hover:text-brand-300";

// SETTINGS_PATH now lives in config/nav.js alongside NOTIF_PATH and HOME_BY_ROLE —
// the Sidebar needs the same table, and keeping a private copy here is what let the
// two halves of the shell disagree about where "my account" is.

// ---------------------------------------------------------------------------
// Reusable popover — closes on outside click / Escape, NOT on inner clicks.
// ---------------------------------------------------------------------------
function Popover({ trigger, buttonClass, ariaLabel, children, align = "right", width = "w-80" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Force-close on navigation. A notification row calls close() and navigate()
  // in the same commit, which is the exact interleaving that leaves a
  // framer-motion exit unresolved (see the drawer in DashboardLayout).
  const { pathname } = useLocation();
  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <div className="relative" ref={ref}>
      {/* `title` is what actually produces a hover tooltip — `aria-label` only
          reaches screen readers. Notifications, Preferences and Profile all sit
          on this shared trigger and had aria-label alone, so none of them showed
          a tooltip; the standalone Sign-out button beside them sets both and was
          the only icon in the cluster that did. */}
      <button type="button" aria-label={ariaLabel} title={ariaLabel} aria-expanded={open} onClick={() => setOpen((v) => !v)} className={buttonClass}>
        {trigger}
      </button>
      {/* Inertness comes from LIVE state on this wrapper, not from the exiting
          child: AnimatePresence re-renders exiting children with their stale
          props, so a guard placed on the panel itself could never fire. If an
          exit ever fails to complete, the leftover panel is unclickable rather
          than a sheet that swallows every tap. */}
      <div className={cn(!open && "pointer-events-none")}>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.97 }}
            transition={{ duration: 0.16, ease: "easeOut" }}
            className={cn(
              "absolute z-50 mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-glow dark:border-white/10 dark:bg-ink-900",
              align === "right" ? "right-0" : "left-0",
              width,
              // Mobile: break out to a fixed, near-full-width sheet anchored just
              // below the header so the menu can never run off-screen.
              "max-sm:!fixed max-sm:!inset-x-3 max-sm:!top-[4.25rem] max-sm:!mt-0 max-sm:!w-auto"
            )}
          >
            {typeof children === "function" ? children(() => setOpen(false)) : children}
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Segmented control with a sliding active pill (used in Preferences).
// ---------------------------------------------------------------------------
function Segmented({ value, onChange, options, id }) {
  return (
    <div className="flex gap-1 rounded-xl bg-slate-100 p-1 dark:bg-white/5">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={cn(
              "relative flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors",
              active ? "text-white" : "text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white"
            )}
          >
            {active && (
              <motion.span
                layoutId={`seg-${id}`}
                className="absolute inset-0 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600 shadow-soft"
                transition={{ type: "spring", stiffness: 420, damping: 34 }}
              />
            )}
            <span className="relative z-10 inline-flex items-center gap-1.5">
              {opt.icon && <opt.icon size={14} />}
              {opt.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Command search — filters the current role's tabs and jumps to them.
// ---------------------------------------------------------------------------
function CommandSearch({ role, current }) {
  const navigate = useNavigate();
  const items = useMemo(() => searchableNav(role), [role]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  // Below `sm` the input was `hidden`, so the command palette — the only route to
  // several pages, and the thing ⌘K focuses — did not exist on a phone at all.
  // On mobile it becomes an icon that opens the same search as a sheet.
  const [sheet, setSheet] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    // Rank: label-prefix (0) > label-substring (1) > group (2) > keyword (3).
    // For group/keyword matches we keep the matched word as `_hint` so the user
    // can see WHY a result with no visible match (e.g. "w" → Projects via
    // "crawl") showed up — otherwise the search looks broken.
    const scored = [];
    for (const i of items) {
      const label = i.label.toLowerCase();
      let rank = null;
      let hint = null;
      if (label.startsWith(term)) rank = 0;
      else if (label.includes(term)) rank = 1;
      else if (i.group.toLowerCase().includes(term)) { rank = 2; hint = i.group; }
      else {
        const hit = (i.keywords || "").split(/\s+/).find((k) => k && k.includes(term));
        if (hit) { rank = 3; hint = hit; }
      }
      if (rank !== null) scored.push({ item: i, rank, hint });
    }
    scored.sort((a, b) => a.rank - b.rank || a.item.label.localeCompare(b.item.label));
    return scored.map((s) => ({ ...s.item, _hint: s.hint }));
  }, [q, items]);

  useEffect(() => setActive(0), [q, open]);

  // Never leave the mobile sheet (and its full-screen scrim) up across a
  // navigation, however that navigation was triggered.
  const { pathname: currentPath } = useLocation();
  useEffect(() => { setSheet(false); setOpen(false); }, [currentPath]);

  // Outside-click to close.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  // Cmd/Ctrl+K focuses search anywhere on the page — including on a narrow
  // viewport, where it has to open the sheet first or it focuses nothing.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        // Not while the user is typing. This listened on `document` with no check
        // on the target, so ⌘K/Ctrl+K inside the Notes textarea, a table's search
        // box or any other field opened the palette instead of doing what the
        // field expects (on macOS, Ctrl+K is "delete to end of line").
        const t = e.target;
        if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) {
          // …unless it's the palette's own input, where ⌘K should still focus it.
          if (t !== inputRef.current) return;
        }
        e.preventDefault();
        setSheet(true);
        setOpen(true);
        setTimeout(() => inputRef.current?.focus(), 0);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const closeSheet = () => {
    setSheet(false);
    setOpen(false);
    setQ("");
  };

  const go = (item) => {
    if (!item) return;
    navigate(item.to);
    setQ("");
    setOpen(false);
    setSheet(false);
    inputRef.current?.blur();
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(results[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setSheet(false);
      inputRef.current?.blur();
    }
  };

  return (
    <>
      {/* mobile trigger — the inline input only exists from `sm` up */}
      <button
        type="button"
        aria-label="Search pages"
        onClick={() => { setSheet(true); setOpen(true); setTimeout(() => inputRef.current?.focus(), 0); }}
        className={cn(ICON_BTN, "sm:hidden")}
      >
        <Search size={18} />
      </button>

      {/* mobile scrim — a plain conditional, NOT inside AnimatePresence, so it
          is removed synchronously and cannot be left behind mid-exit */}
      {sheet && (
        <div
          onClick={closeSheet}
          className="fixed inset-0 z-[75] bg-slate-900/50 sm:hidden"
        />
      )}

      <div
        ref={wrapRef}
        className={cn(
          "relative flex-1",
          sheet
            ? "max-sm:fixed max-sm:inset-x-3 max-sm:top-3 max-sm:z-[80] max-sm:flex-none"
            : "hidden sm:block"
        )}
      >
      <Search size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search pages…  (⌘K)"
        className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-10 pr-4 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-4 focus:ring-brand-500/10 dark:border-white/10 dark:bg-white/5 dark:text-white dark:focus:bg-white/10 sm:max-w-md"
      />

      {/* Same live-state inertness guard as Popover — go() navigates and closes
          in one commit, so the results panel is exposed to the same unresolved-
          exit hazard. */}
      <div className={cn(!open && "pointer-events-none")}>
      <AnimatePresence>
        {open && q.trim() && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.16, ease: "easeOut" }}
            className="absolute left-0 z-50 mt-2 w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-glow dark:border-white/10 dark:bg-ink-900 sm:max-w-md"
          >
            {/* current location breadcrumb */}
            <div className="flex items-center gap-1.5 border-b border-slate-100 px-3.5 py-2.5 text-xs dark:border-white/10">
              <span className="font-medium text-slate-400">{current.section}</span>
              <ChevronRight size={12} className="text-slate-300 dark:text-slate-600" />
              <span className="font-semibold text-slate-700 dark:text-slate-200">{current.label}</span>
            </div>

            {results.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-slate-400">No pages match “{q}”.</div>
            ) : (
              <ul className="max-h-80 overflow-y-auto p-1.5">
                {results.map((item, i) => {
                  const isCurrent = item.to === current.to;
                  return (
                    <li key={item.to}>
                      <button
                        type="button"
                        onMouseEnter={() => setActive(i)}
                        onClick={() => go(item)}
                        className={cn(
                          "flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left transition-colors",
                          i === active ? "bg-brand-50 dark:bg-brand-500/10" : "hover:bg-slate-50 dark:hover:bg-white/5"
                        )}
                      >
                        <span
                          className={cn(
                            "grid h-8 w-8 shrink-0 place-items-center rounded-lg",
                            i === active
                              ? "bg-brand-600 text-white"
                              : "bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-slate-300"
                          )}
                        >
                          <item.icon size={16} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="flex items-center gap-2 text-sm font-medium text-slate-800 dark:text-white">
                            {item.label}
                            {isCurrent && (
                              <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[0.625rem] font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                                current
                              </span>
                            )}
                          </span>
                          {item._hint && (
                            <span className="mt-0.5 block truncate text-xs text-slate-400">
                              matches “{item._hint}”
                            </span>
                          )}
                        </span>
                        <span className="shrink-0 text-xs text-slate-400">{item.group}</span>
                        {i === active && <CornerDownLeft size={14} className="shrink-0 text-brand-500" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Notifications — the header bell. Reads from the shared notifications store
// (lib/notifications), so it stays in sync with the full Notifications page.
// ---------------------------------------------------------------------------
function NotificationsMenu({ role }) {
  // useNotifications() is what mounts the store and starts the poll; the state
  // hook also carries loading/error and the server's own unread total.
  useNotifications();
  const { items: notifs, unread, loading, error } = useNotificationsState();
  const navigate = useNavigate();
  // The PERMISSION itself ("granted" | "denied" | "default" | "unsupported"),
  // not a boolean. A denied site can never be re-prompted from script, so the
  // banner has to say something different in that case rather than keep offering
  // a button that cannot work.
  const [alertPerm, setAlertPerm] = useState(
    typeof Notification === "undefined" ? "unsupported" : Notification.permission
  );
  const alertsOn = alertPerm === "granted";
  // The dropdown is a preview — the full list lives on the Notifications page.
  const preview = notifs.slice(0, 6);
  const allPath = NOTIF_PATH[role] || NOTIF_PATH.tester;

  // Every outcome is reported. This used to bail out silently when Notification
  // was undefined, ignore a "denied" result, and swallow any throw in an empty
  // catch — so on a site that had already blocked notifications the button did
  // nothing at all, showed no prompt (the browser resolves an already-denied
  // request instantly), and gave no clue why.
  const enableAlerts = async () => {
    if (typeof Notification === "undefined") {
      setAlertPerm("unsupported");
      // Notifications need a secure context, so plain http is the usual reason
      // the API is missing rather than the browser genuinely lacking it.
      const insecure =
        typeof window !== "undefined" &&
        window.location.protocol !== "https:" &&
        !["localhost", "127.0.0.1"].includes(window.location.hostname);
      toastError(
        insecure
          ? "Desktop alerts need a secure (https) connection."
          : "This browser doesn't support desktop alerts."
      );
      return;
    }
    if (Notification.permission === "denied") {
      setAlertPerm("denied");
      toastInfo("Desktop alerts are blocked for this site. Allow notifications in your browser's site settings, then try again.");
      return;
    }
    try {
      const res = await Notification.requestPermission();
      setAlertPerm(res);
      if (res === "granted") {
        toastSuccess("Desktop alerts are on.");
        // Prove it immediately — a permission grant with no visible result is
        // indistinguishable from the silent failure this replaces.
        try {
          new Notification("Desktop alerts enabled", {
            body: "You'll get an alert here the moment a report comes in.",
            tag: "aiqa-alerts-enabled",
          });
        } catch (e) {
          /* the grant still stands even if this one alert can't be shown */
        }
      } else if (res === "denied") {
        toastInfo("Desktop alerts were blocked. You can allow them in your browser's site settings.");
      } else {
        toastInfo("Desktop alerts weren't enabled — the prompt was dismissed.");
      }
    } catch (e) {
      toastError(e?.message || "Couldn't turn on desktop alerts.");
    }
  };

  return (
    <Popover
      ariaLabel="Notifications"
      buttonClass={ICON_BTN}
      width="w-[22rem]"
      trigger={
        <>
          <Bell size={18} />
          {unread > 0 && (
            // Capped, like the assistant's badge: an uncapped 3-4 digit count
            // stretched this pill across the bell it is meant to annotate.
            <span className="absolute -right-1 -top-1 grid h-[18px] min-w-[18px] place-items-center rounded-full bg-rose-500 px-1 text-[0.625rem] font-bold text-white ring-2 ring-white dark:ring-ink-950">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </>
      }
    >
      {(close) => (
        <>
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-white/10">
            <div>
              <p className="text-sm font-semibold text-slate-800 dark:text-white">Notifications</p>
              <p className="text-xs text-slate-400">{unread} unread</p>
            </div>
            {unread > 0 && (
              <button
                type="button"
                onClick={markAllRead}
                className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 transition-colors hover:border-brand-300 hover:text-brand-600 dark:border-white/10 dark:text-slate-300 dark:hover:text-brand-300"
              >
                <Check size={13} /> Mark all read
              </button>
            )}
          </div>

          {!alertsOn && (
            <div className="m-3 flex items-center gap-3 rounded-xl border border-brand-200 bg-brand-50/70 px-3 py-2.5 dark:border-brand-500/20 dark:bg-brand-500/10">
              <BellRing size={16} className="shrink-0 text-brand-600 dark:text-brand-300" />
              {alertPerm === "denied" ? (
                // No Enable button here on purpose: once a site is blocked the
                // browser refuses to prompt again, so the only way back is the
                // user's own site settings. Offering the button would be a lie.
                <p className="flex-1 text-xs text-slate-600 dark:text-slate-300">
                  Desktop alerts are blocked for this site. Allow notifications in your
                  browser&apos;s site settings to get an alert when a report comes in.
                </p>
              ) : (
                <>
                  <p className="flex-1 text-xs text-slate-600 dark:text-slate-300">Get a desktop alert the moment a report comes in.</p>
                  <button
                    type="button"
                    onClick={enableAlerts}
                    className="shrink-0 rounded-lg bg-brand-600 px-2.5 py-1 text-xs font-semibold text-white transition-colors hover:bg-brand-500"
                  >
                    Enable
                  </button>
                </>
              )}
            </div>
          )}

          <ul className="max-h-80 overflow-y-auto px-2 pb-2">
            {preview.length === 0 ? (
              <li className="px-3 py-8 text-center text-sm text-slate-400">
                {/* "All caught up" is the one thing we must not say when the
                    request failed or hasn't landed yet. */}
                {error
                  ? "Couldn't load notifications."
                  : loading
                    ? "Loading…"
                    : "You're all caught up."}
              </li>
            ) : (
              preview.map((n) => {
                const meta = NOTIF_TYPES[n.type] || NOTIF_TYPES.info;
                const Icon = meta.icon;
                return (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => {
                        markRead(n.id);
                        // Every notifier stores a deep link; following it is the
                        // whole point of the row. Only close when we're moving.
                        if (n.link) {
                          close();
                          openNotificationLink(n.link, navigate);
                        }
                      }}
                      className={cn(
                        "flex w-full items-start gap-3 rounded-xl px-2.5 py-2.5 text-left transition-colors hover:bg-slate-50 dark:hover:bg-white/5",
                        !n.read && "bg-brand-50/50 dark:bg-brand-500/[0.06]"
                      )}
                    >
                      <span className={cn("mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg", meta.tone)}>
                        <Icon size={16} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-slate-800 dark:text-white">{n.title}</span>
                          {!n.read && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />}
                        </span>
                        <span className="block truncate text-xs text-slate-500 dark:text-slate-400">{n.body}</span>
                        <span className="block text-[0.6875rem] text-slate-400">{timeAgo(n.createdAt)}</span>
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>

          <Link
            to={allPath}
            onClick={close}
            className="flex items-center justify-center gap-1.5 border-t border-slate-100 px-4 py-3 text-sm font-semibold text-brand-600 transition-colors hover:bg-brand-50 dark:border-white/10 dark:text-brand-300 dark:hover:bg-brand-500/10"
          >
            View all notifications <ArrowRight size={15} />
          </Link>
        </>
      )}
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// Preferences — theme + text size + density
// ---------------------------------------------------------------------------
function PreferencesMenu() {
  const { theme, setTheme, textSize, setTextSize, density, setDensity } = useTheme();
  return (
    <Popover ariaLabel="Display preferences" buttonClass={ICON_BTN} width="w-72" trigger={<SlidersHorizontal size={18} />}>
      <div className="space-y-4 p-4">
        <p className="text-sm font-semibold text-slate-800 dark:text-white">Display</p>

        <div className="space-y-1.5">
          <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400">Theme</p>
          <Segmented
            id="theme"
            value={theme}
            onChange={setTheme}
            options={[
              { value: "light", label: "Light", icon: Sun },
              { value: "dark", label: "Dark", icon: Moon },
            ]}
          />
        </div>

        <div className="space-y-1.5">
          <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400">Text size</p>
          <Segmented
            id="text"
            value={textSize}
            onChange={setTextSize}
            options={[
              { value: "s", label: "S" },
              { value: "m", label: "M" },
              { value: "l", label: "L" },
            ]}
          />
        </div>

        <div className="space-y-1.5">
          <p className="text-[0.6875rem] font-semibold uppercase tracking-wider text-slate-400">Density</p>
          <Segmented
            id="density"
            value={density}
            onChange={setDensity}
            options={[
              { value: "compact", label: "Compact" },
              { value: "default", label: "Default" },
              { value: "roomy", label: "Roomy" },
            ]}
          />
        </div>
      </div>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Profile — the top-right corner. This was an anonymous <User> icon linking to
// settings, so nothing in the header said who was signed in; the only avatar and
// name in the product sat at the BOTTOM of the left sidebar, which is not where
// anyone looks for their own account.
// ---------------------------------------------------------------------------
function ProfileMenu({ role, settingsPath, onSignOut }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const displayName =
    user?.name || (user?.email ? user.email.split("@")[0] : ROLE_LABEL[role] || "User");
  // Same derivation as the sidebar avatar, so one person is never two different
  // monograms in the same session.
  const initials = displayName.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  const roleLabel = ROLE_LABEL[role] || "Tester";

  const AVATAR =
    "grid place-items-center rounded-full bg-gradient-to-br from-brand-400 to-brand-600 font-bold text-white";

  return (
    <Popover
      ariaLabel={`Account — ${displayName}`}
      width="w-64"
      buttonClass={cn(ICON_BTN, "!border-transparent !bg-transparent p-0 hover:!shadow-none")}
      trigger={<span className={cn(AVATAR, "h-9 w-9 text-xs shadow-soft ring-2 ring-white dark:ring-ink-950")}>{initials}</span>}
    >
      {(close) => (
        <>
          {/* This header used to be a static div — nothing here was clickable, so
              the menu only ever offered two working actions (Settings, Sign out)
              though it reads like it should offer three (Profile, Settings, Sign
              out). Profile info lives INSIDE the Settings page (see "Profile
              information" in dashboard/Settings.jsx) rather than on a page of its
              own, so this now navigates there too — the same destination as the
              Settings item below, reached the way a profile card is normally
              expected to work. */}
          <button
            type="button"
            onClick={() => { close(); navigate(settingsPath); }}
            className="flex w-full items-center gap-3 border-b border-slate-100 px-4 py-3 text-left transition-colors hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5"
          >
            <span className={cn(AVATAR, "h-10 w-10 shrink-0 text-sm")}>{initials}</span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-slate-800 dark:text-white">{displayName}</p>
              {/* The email identifies the account; the role says what it can do.
                  Falls back to the role alone when we only know the name. */}
              <p className="truncate text-xs text-slate-400">{user?.email || roleLabel}</p>
            </div>
            <ChevronRight size={14} className="shrink-0 text-slate-300 dark:text-slate-600" />
          </button>
          <div className="p-1.5">
            <button
              type="button"
              onClick={() => { close(); navigate(settingsPath); }}
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-white/5"
            >
              <Settings size={16} className="shrink-0 text-slate-400" /> Settings
            </button>
            <button
              type="button"
              onClick={() => { close(); onSignOut(); }}
              className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm text-slate-700 transition-colors hover:bg-rose-50 hover:text-rose-600 dark:text-slate-200 dark:hover:bg-rose-500/10 dark:hover:text-rose-300"
            >
              <LogOut size={16} className="shrink-0 text-slate-400" /> Sign out
            </button>
          </div>
        </>
      )}
    </Popover>
  );
}

export default function Topbar({ role, onMenu }) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { pathname } = useLocation();
  // Sign out ASKS FIRST. There were two unguarded ways to end a session in this
  // header — a menu item and a bare icon button a few pixels from the avatar —
  // and hitting either from the Notes page took an unsaved draft with it.
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  const { section, item } = matchNavItem(role, pathname);
  const current = { section, label: item?.label || "—", to: item?.to || HOME_BY_ROLE[role] };
  const settingsPath = SETTINGS_PATH[role] || "/dashboard/settings";
  // One sign-out path for both the menu item and the icon beside it — two copies
  // of logout()+navigate() is two places to forget one of the steps.
  const signOut = () => {
    setConfirmSignOut(false);
    logout();
    navigate("/login");
  };

  return (
    // The sign-out confirmation used to render INSIDE this header, and that
    // broke it: `backdrop-blur-xl` below is a `backdrop-filter`, and per the
    // CSS spec an element with `backdrop-filter` becomes the CONTAINING BLOCK
    // for any `position: fixed` descendant — so the modal's `fixed inset-0`
    // resolved against this 64px-tall header's own box, not the viewport,
    // squeezing it into a sliver overlapping the header instead of covering
    // the screen. Rendering it as a SIBLING of <header>, outside the filtered
    // element, is the fix — moving the dialog itself has no such ancestor.
    <>
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-slate-200 bg-white/80 px-4 backdrop-blur-xl dark:border-white/10 dark:bg-ink-950/80 sm:px-6">
      {/* mobile menu */}
      <button
        type="button"
        onClick={onMenu}
        aria-label="Open menu"
        className="grid h-10 w-10 place-items-center rounded-xl border border-slate-200 text-slate-600 dark:border-white/10 dark:text-slate-300 lg:hidden"
      >
        <Menu size={20} />
      </button>

      {/* breadcrumb — where you are */}
      <nav aria-label="Breadcrumb" className="hidden items-center gap-1.5 whitespace-nowrap md:flex">
        <Link to={HOME_BY_ROLE[role]} className="text-sm font-medium text-slate-400 transition-colors hover:text-brand-600 dark:hover:text-brand-300">
          {section}
        </Link>
        <ChevronRight size={14} className="text-slate-300 dark:text-slate-600" />
        <span className="text-sm font-semibold text-slate-800 dark:text-white">{current.label}</span>
      </nav>

      <div className="hidden h-6 w-px bg-slate-200 dark:bg-white/10 md:block" />

      {/* search */}
      <CommandSearch role={role} current={current} />
      <div className="flex-1 sm:hidden" />

      {/* right cluster: notifications · preferences · profile · logout */}
      <div className="flex items-center gap-1.5">
        <NotificationsMenu role={role} />
        <PreferencesMenu />
        <ProfileMenu role={role} settingsPath={settingsPath} onSignOut={() => setConfirmSignOut(true)} />
        <button
          type="button"
          aria-label="Sign out"
          title="Sign out"
          onClick={() => setConfirmSignOut(true)}
          className={cn(ICON_BTN, "hover:!border-rose-300 hover:!text-rose-600 dark:hover:!border-rose-500/40 dark:hover:!text-rose-300")}
        >
          <LogOut size={18} />
        </button>
      </div>

    </header>
    <ConfirmDialog
      open={confirmSignOut}
      title="Sign out?"
      body="You'll need to sign in again to get back to your dashboard. Anything you haven't saved will be lost."
      confirmLabel="Sign out"
      onConfirm={signOut}
      onCancel={() => setConfirmSignOut(false)}
    />
    </>
  );
}
